/**
 * A/B Test bridge: mirrors a Convert-applied body class into a cart attribute
 * so a checkout extension can render the matching variant.
 *
 * Convert is responsible for bucket assignment and applying the body class.
 * For each enabled test (configured in theme settings, emitted by
 * snippets/ab-test.liquid into window.statelyVars.abTest.tests):
 *
 *   1. Look for `<classPrefix><bucket>` on document.body. If found, sync.
 *   2. Otherwise, MutationObserver on body[class] + fallback timeout.
 *      On timeout: do nothing — checkout falls back to control. This is the
 *      desired behavior when consent is denied or Convert never fires.
 *
 * On detection:
 *   - GET /cart.js, only POST /cart/update.js if attributes[`_ab_<name>`]
 *     is missing or stale (idempotent across navigations).
 *   - Inject a hidden input `attributes[_ab_<name>]=<bucket>` into every
 *     `form[action*="/cart/add"]` so first ATC carries the attribute even if
 *     the async POST hasn't landed. Re-run on DOM mutations to cover
 *     dynamically-injected forms.
 *
 * QA hooks:
 *   - URL param `?ab_<name>=A|B` forces a bucket (case-insensitive match
 *     against configured buckets).
 *   - Skipped entirely in Shopify.designMode.
 *   - Bucket exposed at window.stately.abTest[<name>] = '<bucket>'.
 *   - Publishes 'ab:assigned' event via the global pubsub.
 *
 * The cart attribute key is prefixed with `_` so it's hidden from order
 * admin and customer-facing emails.
 */

const CART_ATTR_PREFIX = '_ab_';
const PUBSUB_EVENT = 'ab:assigned';

function getTests() {
  const cfg = window.statelyVars && window.statelyVars.abTest;
  if (!cfg || !Array.isArray(cfg.tests)) return [];
  return cfg.tests.filter(
    (t) => t && t.name && t.classPrefix && Array.isArray(t.buckets) && t.buckets.length > 0
  );
}

function isDesignMode() {
  return !!(window.Shopify && window.Shopify.designMode);
}

function urlOverrideFor(test) {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ab_' + test.name);
    if (!raw) return null;
    const match = test.buckets.find((b) => b.toLowerCase() === raw.toLowerCase());
    return match || null;
  } catch (_e) {
    return null;
  }
}

function findBucketInBodyClass(test) {
  const classes = document.body.className.split(/\s+/);
  for (const cls of classes) {
    if (!cls.startsWith(test.classPrefix)) continue;
    const suffix = cls.slice(test.classPrefix.length);
    if (test.buckets.indexOf(suffix) !== -1) return suffix;
  }
  return null;
}

function attrKey(test) {
  return CART_ATTR_PREFIX + test.name;
}

async function syncCartAttribute(test, bucket) {
  const key = attrKey(test);
  try {
    const cartRes = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();
    const current = cart && cart.attributes ? cart.attributes[key] : undefined;
    if (current === bucket) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes: { [key]: bucket } }),
    });
  } catch (_e) {
    // Network errors are non-fatal: the hidden input on cart forms still
    // covers the first ATC, and the next page load will retry.
  }
}

function injectHiddenInputs(test, bucket) {
  const inputName = 'attributes[' + attrKey(test) + ']';
  const forms = document.querySelectorAll('form[action*="/cart/add"]');
  forms.forEach((form) => {
    const existing = form.querySelector('input[name="' + inputName + '"]');
    if (existing) {
      if (existing.value !== bucket) existing.value = bucket;
      return;
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = inputName;
    input.value = bucket;
    input.setAttribute('data-ab-injected', test.name);
    form.appendChild(input);
  });
}

function watchForNewCartForms(tests, bucketsByName) {
  const observer = new MutationObserver((mutations) => {
    let needsRescan = false;
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches && node.matches('form[action*="/cart/add"]')) {
          needsRescan = true;
          break;
        }
        if (node.querySelector && node.querySelector('form[action*="/cart/add"]')) {
          needsRescan = true;
          break;
        }
      }
      if (needsRescan) break;
    }
    if (!needsRescan) return;
    tests.forEach((t) => {
      const b = bucketsByName[t.name];
      if (b) injectHiddenInputs(t, b);
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function announce(test, bucket) {
  window.stately = window.stately || {};
  window.stately.abTest = window.stately.abTest || {};
  window.stately.abTest[test.name] = bucket;

  if (typeof publish === 'function') {
    try {
      publish(PUBSUB_EVENT, { name: test.name, bucket });
    } catch (_e) {
      // pubsub failures must not break theme JS
    }
  }
}

function activateBucket(test, bucket, bucketsByName) {
  if (bucketsByName[test.name] === bucket) return;
  bucketsByName[test.name] = bucket;
  injectHiddenInputs(test, bucket);
  syncCartAttribute(test, bucket);
  announce(test, bucket);
}

function resolveTest(test, bucketsByName) {
  const override = urlOverrideFor(test);
  if (override) {
    activateBucket(test, override, bucketsByName);
    return;
  }

  const immediate = findBucketInBodyClass(test);
  if (immediate) {
    activateBucket(test, immediate, bucketsByName);
    return;
  }

  let resolved = false;
  const observer = new MutationObserver(() => {
    if (resolved) return;
    const found = findBucketInBodyClass(test);
    if (!found) return;
    resolved = true;
    observer.disconnect();
    clearTimeout(fallbackTimer);
    activateBucket(test, found, bucketsByName);
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  const fallbackMs = typeof test.fallbackMs === 'number' && test.fallbackMs > 0 ? test.fallbackMs : 3000;
  const fallbackTimer = setTimeout(() => {
    if (resolved) return;
    resolved = true;
    observer.disconnect();
    // Intentional no-op: do not assign or write a cart attribute.
    // Checkout extension will see no attribute and render control.
  }, fallbackMs);
}

function init() {
  if (isDesignMode()) return;
  const tests = getTests();
  if (tests.length === 0) return;

  const bucketsByName = {};
  tests.forEach((t) => resolveTest(t, bucketsByName));
  watchForNewCartForms(tests, bucketsByName);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
